import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Search, Building2, Package, CheckCircle, ArrowRight, Star } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
import { ProcessSlider } from '../components/ProcessSlider';
import { TestimonialSlider } from '../components/TestimonialSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export const Home = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 fade-in-up">
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight">
                Protect Your Brand with
                <span className="text-yellow-400"> Professional </span>
                Trademark Services
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                Secure your intellectual property rights with America's most trusted trademark registration experts. Fast, affordable, and hassle-free trademark protection starts here.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onOpenPopup}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
                >
                  Get Started Now
                </button>
                <Link
                  to="/services/trademark-search"
                  className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 text-center"
                >
                  Free Trademark Search
                </Link>
              </div>
              <div className="flex items-center space-x-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">10,000+</div>
                  <div className="text-white/80">Trademarks Filed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">98%</div>
                  <div className="text-white/80">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">24/7</div>
                  <div className="text-white/80">Support</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-red-100 p-3 rounded-full">
                    <Shield className="w-8 h-8 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Why Choose Us?</h3>
                    <p className="text-gray-600">Industry-leading expertise</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Expert trademark attorneys on staff</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Comprehensive search reports included</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Fast USPTO filing within 24-48 hours</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Dedicated case manager for your application</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Money-back guarantee on our services</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Your Brand Deserves <span className="text-red-600">Maximum Protection</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                In today's competitive marketplace, protecting your brand identity is not just important—it's essential. A registered trademark gives you exclusive rights to use your brand name, logo, or slogan across the United States, preventing others from capitalizing on your hard-earned reputation.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Global Mark Registry, we've simplified the trademark registration process. Our team of experienced trademark attorneys handles everything from comprehensive searches to USPTO filing, ensuring your application has the highest chance of approval. Don't risk losing your brand to competitors—secure your trademark today.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <ArrowRight className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Nationwide protection for your brand identity</span>
                </li>
                <li className="flex items-start space-x-3">
                  <ArrowRight className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Legal grounds to stop infringement</span>
                </li>
                <li className="flex items-start space-x-3">
                  <ArrowRight className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Increased brand value and credibility</span>
                </li>
                <li className="flex items-start space-x-3">
                  <ArrowRight className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Foundation for international expansion</span>
                </li>
              </ul>
              <button
                onClick={onOpenPopup}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl inline-flex items-center space-x-2"
              >
                <span>Protect Your Brand Now</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop"
                alt="Business protection"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-gray-900 p-6 rounded-xl shadow-xl max-w-xs">
                <div className="text-3xl font-bold mb-1">$45+</div>
                <div className="text-sm font-semibold">Starting Price for Complete Protection</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive <span className="text-red-600">Trademark Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial search to final registration, we provide end-to-end trademark solutions tailored to your business needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link to="/services/trademark-registration" className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 card-hover">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Trademark Registration</h3>
              <p className="text-gray-600 mb-4">
                Complete trademark registration service with comprehensive search, USPTO filing, and ongoing monitoring. Protect your brand identity nationwide.
              </p>
              <div className="flex items-center text-red-600 font-semibold">
                <span>Learn More</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </Link>

            <Link to="/services/copyright-registration" className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 card-hover">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Copyright Registration</h3>
              <p className="text-gray-600 mb-4">
                Secure legal protection for your creative works including books, music, software, and artistic creations with official copyright registration.
              </p>
              <div className="flex items-center text-red-600 font-semibold">
                <span>Learn More</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </Link>

            <Link to="/services/trademark-search" className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 card-hover">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Search className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Trademark Search</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive trademark search to identify potential conflicts before filing. Reduce rejection risk and save time with our detailed analysis.
              </p>
              <div className="flex items-center text-red-600 font-semibold">
                <span>Learn More</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </Link>

            <Link to="/services/company-formation" className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 card-hover">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Company Formation</h3>
              <p className="text-gray-600 mb-4">
                Start your business the right way with our comprehensive company formation services. LLC, Corporation, or Partnership—we handle it all.
              </p>
              <div className="flex items-center text-red-600 font-semibold">
                <span>Learn More</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </Link>

            <Link to="/services/amazon-brand-registry" className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 card-hover">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Package className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Amazon Brand Registry</h3>
              <p className="text-gray-600 mb-4">
                Protect your products on Amazon's marketplace. Get enrolled in Amazon Brand Registry to combat counterfeits and control your brand content.
              </p>
              <div className="flex items-center text-red-600 font-semibold">
                <span>Learn More</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </Link>

            <div 
              className="relative rounded-xl p-8 shadow-lg text-white flex flex-col justify-center overflow-hidden"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-red-600/95 to-red-700/95"></div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3">Not Sure Where to Start?</h3>
                <p className="mb-6">
                  Our trademark experts are here to guide you through the entire process. Get a free consultation today!
                </p>
                <button
                  onClick={onOpenPopup}
                  className="bg-white text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Schedule Free Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Simple <span className="text-red-600">3-Step Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting your trademark registered has never been easier. Our streamlined process ensures fast and efficient results.
            </p>
          </div>
          <ProcessSlider />
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Transparent <span className="text-red-600">Pricing Plans</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the package that best fits your needs. All plans include expert guidance and support throughout the process.
            </p>
          </div>
          <PricingSection onOpenPopup={onOpenPopup} />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our <span className="text-red-600">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of satisfied clients who have successfully protected their brands with Global Mark Registry.
            </p>
          </div>
          <TestimonialSlider />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - FAQ Content */}
            <div className="space-y-6">
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Frequently Asked <span className="text-red-600">Questions</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Get answers to the most common questions about trademark registration and our services.
                </p>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                {[
                  {
                    q: 'How long does trademark registration take?',
                    a: 'The entire USPTO process typically takes 8-12 months from filing to registration. We file your application within 24-48 hours of receiving your information.'
                  },
                  {
                    q: 'What is included in your trademark services?',
                    a: 'Our services include comprehensive trademark search, application preparation, USPTO filing, office action responses, and ongoing monitoring throughout the entire process.'
                  },
                  {
                    q: 'Do I really need a trademark for my business?',
                    a: 'Yes, if you want exclusive rights to your brand name, logo, or slogan. A trademark prevents others from using similar names and provides legal protection against infringement.'
                  },
                  {
                    q: 'What happens if my application is rejected?',
                    a: 'While we have a 98% approval rate, if your application receives an office action, we respond on your behalf at no additional charge in our Deluxe and Platinum packages.'
                  },
                  {
                    q: 'How much does trademark registration cost?',
                    a: 'Our service fees start at $45, plus USPTO filing fees ($250-$350 per class). We offer transparent pricing with no hidden costs.'
                  }
                ].map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg shadow-md border-none">
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                      <span className="text-lg font-bold text-gray-900">{faq.q}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-gray-600 leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="pt-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center text-red-600 font-semibold text-lg hover:text-red-700 transition-colors"
                >
                  Have more questions? Contact us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=1000&fit=crop"
                alt="Trademark questions and answers"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-gray-900 p-6 rounded-xl shadow-xl max-w-xs">
                <div className="text-3xl font-bold mb-1">98%</div>
                <div className="text-sm font-semibold">Success Rate with Expert Guidance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Don't wait until it's too late. Secure your trademark today and gain peace of mind knowing your brand is legally protected.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onOpenPopup}
              className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
            >
              Start Your Application
            </button>
            <Link
              to="/contact"
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 inline-block"
            >
              Contact Our Experts
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};